/**
 * Helpers for "quick collect" amount suggestions from Proform / shipping
 * `productandprice` lines (qty_containers, raw_totalprice, raw_totalpricecont, per_container).
 * Mirrors back/services/shippingDerivedTotalsService.js computeProductBlock semantics.
 */

export function normalizeProductandpriceLines(raw) {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.filter((x) => x && typeof x === "object");
}

export function lineQtyContainers(line) {
  const q = Math.round(Number(line && line.qty_containers));
  return Number.isFinite(q) && q > 0 ? q : 0;
}

/**
 * `per_container === false` (solo ese valor): el total de productos/precio es el de **toda la línea**
 * (se reparte entre `qty_containers`). Cualquier otro valor (true / undefined): el total de productos
 * es **por contenedor** y el total de línea = ese importe × qty (igual que computeProductBlock en el back).
 *
 * @returns {{ qty: number, lineTotal: number, perContainer: number, priceIsPerContainer: boolean }}
 */
export function lineEconomics(line) {
  const qc = lineQtyContainers(line);
  if (qc <= 0) {
    return { qty: 0, lineTotal: 0, perContainer: 0, priceIsPerContainer: true };
  }

  const priceIsPerContainer = line.per_container !== false;

  const rtc = Number(line.raw_totalpricecont);
  if (Number.isFinite(rtc) && rtc > 0) {
    return {
      qty: qc,
      lineTotal: rtc,
      perContainer: rtc / qc,
      priceIsPerContainer,
    };
  }

  let rawProducts = 0;
  const products = Array.isArray(line.products) ? line.products : [];
  for (const p of products) {
    if (!p || typeof p !== "object") continue;
    const ut = Number(p.unit_totalprice);
    if (Number.isFinite(ut)) {
      rawProducts += ut;
      continue;
    }
    const uq = Number(p.unit_qty) || 0;
    const up = Number(p.unit_price) || 0;
    rawProducts += uq * up;
  }

  let raw = Number(line.raw_totalprice);
  if (!Number.isFinite(raw) || raw <= 0) raw = rawProducts;
  if (!Number.isFinite(raw) || raw <= 0) {
    const fallbacks = [
      line.total,
      line.subtotal,
      line.amount,
      line.line_total,
      line.linetotal,
      line.price_total,
    ];
    for (const f of fallbacks) {
      const n = Number(f);
      if (Number.isFinite(n) && n > 0) {
        raw = n;
        break;
      }
    }
  }
  if (!Number.isFinite(raw) || raw <= 0) {
    return { qty: qc, lineTotal: 0, perContainer: 0, priceIsPerContainer };
  }

  const lineTotal = priceIsPerContainer ? raw * qc : raw;
  return { qty: qc, lineTotal, perContainer: lineTotal / qc, priceIsPerContainer };
}

export function grandTotalFromLines(lines) {
  return normalizeProductandpriceLines(lines).reduce((s, line) => s + lineEconomics(line).lineTotal, 0);
}

export function totalContainersFromLines(lines) {
  return normalizeProductandpriceLines(lines).reduce((s, line) => s + lineEconomics(line).qty, 0);
}

/**
 * @param {Array<{ qty: number, perContainer: number }>} metas
 * @param {number[]} picks count per line (0..qty)
 */
export function sumPerLinePicks(metas, picks) {
  if (!Array.isArray(metas) || !Array.isArray(picks)) return 0;
  let sum = 0;
  for (let i = 0; i < metas.length; i++) {
    const m = metas[i];
    const n = Math.round(Number(picks[i]));
    const max = m && m.qty ? m.qty : 0;
    const use = Number.isFinite(n) ? Math.min(Math.max(0, n), max) : 0;
    const pc = m && Number(m.perContainer);
    sum += use * (Number.isFinite(pc) ? pc : 0);
  }
  return sum;
}
